import React, { useState } from 'react';
import { 
  ArrowRight,
  QrCode,
  Shield,
  Users,
  FileText,
  CheckCircle,
  Star,
  Mail,
  Phone,
  ArrowUp,
  Play,
  Download,
  Zap,
  Globe,
  Lock,
  TrendingUp
} from 'lucide-react';

const LandingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  
  // Use environment variable or current domain for login URLs
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

  const handlePurchase = async (planType) => {
    try {
      const origin = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: planType,
          origin_url: origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Det oppstod en feil. Prøv igjen senere.');
    }
  };

  const pricingPlans = {
    monthly: {
      start: { price: 359, annual: false },
      unlimited: { price: 618, annual: false }
    },
    halfyearly: {
      start: { price: 329, annual: false },
      unlimited: { price: 529, annual: false }
    },
    yearly: {
      start: { price: 299, annual: true, savings: '20%' },
      unlimited: { price: 499, annual: true, savings: '20%' }
    }
  };

  const features = [
    {
      icon: QrCode,
      title: 'QR-kode skanning',
      description: 'Medlemmer registrerer oppmøte ved å skanne QR-kode på skytebanen. Rask, enkelt og feilfritt.',
      benefits: ['Fungerer på alle mobiler', 'Automatisk tidsstempel', 'Ingen app-nedlasting nødvendig']
    },
    {
      icon: Shield,
      title: 'Admin dashboard',
      description: 'Komplett administrasjonspanel for standplassledere og klubbadministratorer.',
      benefits: ['Godkjenn treningsøkter', 'Administrere medlemmer', 'Generere rapporter']
    },
    {
      icon: FileText,
      title: 'Digital treningslogg',
      description: 'Automatisk generering av treningslogger som kan brukes til våpensøknader.',
      benefits: ['PDF-eksport', 'Politi-godkjent format', 'Automatisk beregning']
    },
    {
      icon: Users,
      title: 'Medlems-administrasjon',
      description: 'Enkelt system for å administrere medlemmer, roller og tilganger.',
      benefits: ['Automatisk registrering', 'Enkel godkjenningsflyt', 'Rollebasert tilgang']
    },
    {
      icon: Globe,
      title: 'Multi-organisasjon',
      description: 'Støtter flere organisasjoner i samme system med egne innstillinger.',
      benefits: ['Opprette egne baner', 'Tilpasse med egen logo og farger', 'Støtter NSF, DFS, DSSN samtidig']
    },
    {
      icon: Lock,
      title: 'Sikkerhet og personvern',
      description: 'Bygget med sikkerhet i fokus og fullt GDPR-kompatibelt.',
      benefits: ['Krypterte data', 'GDPR-kompatibilitet', 'Sikre servere med backup']
    }
  ];

  const testimonials = [
    {
      name: 'Svolvær Pistolklubb',
      quote: 'AKTIVLOGG har revolusjonert måten vi administrerer treningsaktivitet på. Fra kaos til kontroll på få minutter!',
      role: 'Pistolklubb'
    },
    {
      name: 'Lofoten Sportsskytterklubb', 
      quote: 'Vi valgte AKTIVLOGG for å avlaste skytelederne og klubben for tidkrevende manuelle føringer.',
      role: 'Skytterklubb'
    }
  ];

  const OrderModal = ({ plan, onClose }) => {
    const [formData, setFormData] = useState({
      organizationName: '',
      contactEmail: '',
      contactPhone: '',
      contactName: '',
      memberCount: '',
      message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real implementation, you would send this to your backend
      console.log('Order submitted:', { plan, ...formData });
      
      alert('Takk for bestillingen! Vi kontakter deg innen 24 timer for å fullføre oppsettet.');
      setIsSubmitting(false);
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Bestill {plan === 'start' ? 'START' : 'UBEGRENSET'} plan
          </h2>

          <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-gray-900 mb-2">Din valgte plan:</h3>
            <div className="text-lg">
              <span className="font-bold text-yellow-600">
                {plan === 'start' ? 'START' : 'UBEGRENSET'}
              </span>
              {' - '}
              <span className="font-bold text-gray-900">
                {pricingPlans[selectedPlan][plan].price} kr/mnd
              </span>
              {pricingPlans[selectedPlan][plan].savings && (
                <span className="text-green-600 ml-2">
                  (Spar {pricingPlans[selectedPlan][plan].savings})
                </span>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisasjonsnavn *
                </label>
                <input
                  type="text"
                  required
                  value={formData.organizationName}
                  onChange={(e) => setFormData({...formData, organizationName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="F.eks. Oslo Pistolklubb"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kontaktperson *
                </label>
                <input
                  type="text"
                  required
                  value={formData.contactName}
                  onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Fullt navn"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-post adresse *
                </label>
                <input
                  type="email"
                  required
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="post@klubbnavn.no"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefonnummer *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="+47 123 45 678"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Antall medlemmer (ca.)
              </label>
              <input
                type="number"
                value={formData.memberCount}
                onChange={(e) => setFormData({...formData, memberCount: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="F.eks. 45"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Melding (valgfritt)
              </label>
              <textarea
                rows="3"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Spesielle ønsker eller spørsmål..."
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Hva skjer nå?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Vi kontakter deg innen 24 timer</li>
                <li>• Gratis oppsett og konfigurasjon</li>
                <li>• Komplett opplæring for administratorer</li>
                <li>• 1 måned gratis prøveperiode</li>
                <li>• Ingen bindingstid - kan avbestilles når som helst</li>
              </ul>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Sender bestilling...' : 'Send bestilling'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-yellow-500 rounded-full flex items-center justify-center">
                <QrCode className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">AKTIVLOGG</h1>
                <p className="text-sm text-gray-600">Digital treningslogg</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#funksjoner" className="text-gray-600 hover:text-gray-900">Funksjoner</a>
              <a href="#priser" className="text-gray-600 hover:text-gray-900">Priser</a>
              <a href="mailto:yngve@aktivlogg.no" className="text-gray-600 hover:text-gray-900">Kontakt</a>
              <a 
                href={`${frontendUrl}/login`}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-600 transition-colors"
              >
                Logg inn
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">
            Superenkel digital treningsregistrering
          </h1>
          <h2 className="text-2xl font-light mb-8">
            for norske pistolklubber og skytterlag
          </h2>
          <p className="text-xl max-w-4xl mx-auto mb-10 leading-relaxed">
            Slutt med kostbare medlemskort, ringpermer med skytelogg og blåpapir. I Norge, der NSF og DFS krever nøyaktig dokumentasjon, er framtiden å bare skanne QR-koden med mobilen på skytebanen – og vips, er aktiviteten din registrert og klar for våpensøknaden.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button 
              onClick={() => handlePurchase('start')}
              className="bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors inline-flex items-center"
            >
              Bestill nå <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <a 
              href={`${frontendUrl}/login`}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-800 transition-colors inline-flex items-center"
            >
              <Play className="mr-2 h-5 w-5" />
              Test gratis demo
            </a>
          </div>
        </div>
      </section>

      {/* Features Highlights */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Praktisk digital treningslogg for klubb og medlemmer
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Automatisk generering av treningslogger som kan brukes til våpensøknader og dokumentasjon.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                icon: CheckCircle, 
                text: 'INGEN app-nedlasting nødvendig',
                color: 'text-green-600'
              },
              { 
                icon: Download, 
                text: 'Last ned politi-godkjente PDF-rapporter',
                color: 'text-blue-600'
              },
              { 
                icon: Zap, 
                text: 'Hent ut egen treningslogg med ett klikk',
                color: 'text-yellow-600'
              },
              { 
                icon: QrCode, 
                text: 'Automatisk registrering via QR',
                color: 'text-purple-600'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <item.icon className={`h-12 w-12 mx-auto mb-4 ${item.color}`} />
                <p className="text-gray-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="funksjoner" className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nyttige funksjoner som forenkler hverdagen
            </h2>
            <p className="text-lg text-gray-600">
              Alt klubben trenger for effektiv treningsadministrasjon.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                <feature.icon className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Hvorfor velge AKTIVLOGG?</h2>
            <p className="text-xl">
              Aktivlogg er utviklet i Norge for å møte behovene til pistolklubber og skytterlag.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: TrendingUp,
                title: 'Spar 90% av tiden',
                description: 'Fra timevis manuelt arbeid til sekunder med automatisering.'
              },
              {
                icon: CheckCircle,
                title: 'Bedre oversikt',
                description: 'Få verdifull innsikt i medlemsaktivitet og trender.'
              },
              {
                icon: Shield,
                title: 'Politi-godkjent',
                description: 'Generer dokumentasjon som godkjennes av politiet for våpensøknader.'
              },
              {
                icon: Star,
                title: 'Spesialtilpasset',
                description: 'Utviklet spesielt for skandinaviske skytterlag og idrettslag.'
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <benefit.icon className="h-16 w-16 mx-auto mb-4 text-yellow-400" />
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-blue-100">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Allerede i bruk hos</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.quote}"</p>
                <div className="font-semibold text-gray-900">{testimonial.name}</div>
                <div className="text-sm text-gray-600">{testimonial.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="priser" className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Enkle og forutsigbare priser
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Ingen skjulte kostnader eller overraskelser.
            </p>

            {/* Pricing Toggle */}
            <div className="flex justify-center mb-8">
              <div className="bg-gray-100 p-1 rounded-lg">
                {Object.keys(pricingPlans).map((plan) => (
                  <button
                    key={plan}
                    onClick={() => setSelectedPlan(plan)}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      selectedPlan === plan
                        ? 'bg-yellow-500 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {plan === 'yearly' ? 'Årlig' : plan === 'halfyearly' ? 'Halvårlig' : 'Pr. mnd'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* START Plan */}
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">START</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-gray-900">
                    {pricingPlans[selectedPlan].start.price}
                  </span>
                  <span className="text-lg text-gray-600"> kr/mnd</span>
                  {pricingPlans[selectedPlan].start.savings && (
                    <div className="text-sm text-green-600 font-medium mt-1">
                      Spar {pricingPlans[selectedPlan].start.savings}
                    </div>
                  )}
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  'Enkel QR-kode registrering',
                  'Inntil 100 medlemmer', 
                  'Politi-godkjente PDF-rapporter',
                  'Laste opp startkort/medlemskort',
                  'E-post support',
                  'Gratis online opplæring'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePurchase('start')}
                className="w-full bg-yellow-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-yellow-600 transition-colors"
              >
                BESTILL START
              </button>
            </div>

            {/* UNLIMITED Plan */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-2 border-yellow-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Mest populær
                </span>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">UBEGRENSET</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-gray-900">
                    {pricingPlans[selectedPlan].unlimited.price}
                  </span>
                  <span className="text-lg text-gray-600"> kr/mnd</span>
                  {pricingPlans[selectedPlan].unlimited.savings && (
                    <div className="text-sm text-green-600 font-medium mt-1">
                      Spar {pricingPlans[selectedPlan].unlimited.savings}
                    </div>
                  )}
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  'Alle funksjoner fra START-pakken',
                  'Ubegrenset antall medlemmer',
                  'Avanserte rapporter',
                  'Tilpasset branding',
                  'Prioritert telefon/epost support',
                  'Kontinuerlige oppdateringer'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePurchase('unlimited')}
                className="w-full bg-yellow-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-yellow-600 transition-colors"
              >
                BESTILL UBEGRENSET
              </button>
            </div>
          </div>

          <div className="text-center mt-8 max-w-3xl mx-auto">
            <p className="text-gray-600">
              Velg månedlig betaling for fleksibilitet eller <strong>spar 20%</strong> med årlig betaling. 
              Alle planer inkluderer gratis opplæring og support for å sikre en smidig oppstart.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Klar for å modernisere treningsadministrasjonen?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Bli med i den digitale revolusjonen og gjør treningsregistrering til en fornøyelse for både medlemmer og administratorer.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => handlePurchase('start')}
              className="bg-gray-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-800 transition-colors inline-flex items-center"
            >
              Kom i gang i dag <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <a
              href="mailto:yngve@aktivlogg.no?subject=Spørsmål om Aktivlogg"
              className="bg-transparent border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-900 hover:text-white transition-colors inline-flex items-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Still spørsmål
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-10 w-10 bg-yellow-500 rounded-full flex items-center justify-center">
                  <QrCode className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">AKTIVLOGG</h3>
                  <p className="text-gray-400 text-sm">Digital treningslogg</p>
                </div>
              </div>
              <p className="text-gray-400">
                Stolt utviklet i Norge av og for norske skyttere – kompatibelt med alle enheter og klar for NSF-anbefaling.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Kontakt</h4>
              <div className="space-y-2">
                <a href="mailto:yngve@aktivlogg.no" className="flex items-center text-gray-400 hover:text-white">
                  <Mail className="h-4 w-4 mr-2" />
                  yngve@aktivlogg.no
                </a>
                <a href="tel:+4798055033" className="flex items-center text-gray-400 hover:text-white">
                  <Phone className="h-4 w-4 mr-2" />
                  +47 980 55 033
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Lenker</h4>
              <div className="space-y-2">
                <a href="#funksjoner" className="block text-gray-400 hover:text-white">Funksjoner</a>
                <a href="#priser" className="block text-gray-400 hover:text-white">Priser</a>
                <a href={`${frontendUrl}/login`} className="block text-gray-400 hover:text-white">Logg inn</a>
                <a href="mailto:yngve@aktivlogg.no" className="block text-gray-400 hover:text-white">Support</a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Aktivlogg. Alle rettigheter reservert.</p>
          </div>
        </div>
      </footer>

      {/* Order Modal */}
      {showOrderModal && (
        <OrderModal 
          plan={orderDetails} 
          onClose={() => {
            setShowOrderModal(false);
            setOrderDetails(null);
          }} 
        />
      )}

      {/* Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-yellow-500 text-white p-3 rounded-full shadow-lg hover:bg-yellow-600 transition-colors"
      >
        <ArrowUp className="h-6 w-6" />
      </button>
    </div>
  );
};

export default LandingPage;